import Modal from '../ui/Modal';
import { useState, useRef } from 'react';
import { VENUE_EQUIPMENT_LIST } from '../constants';
import { uploadVenueImage } from '../lib/supabase';

function CityAutocomplete({ value, onChange, placeholder="es. Roma" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);

  const search = async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowSug(false); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&limit=5&countrycodes=it`,
        { headers: { 'Accept-Language': 'it' } }
      );
      const data = await res.json();
      const cities = [...new Map(
        data.map(d => d.address?.city || d.address?.town || d.address?.village || d.address?.municipality)
          .filter(Boolean).map(c => [c, c])
      ).values()];
      setSuggestions(cities);
      setShowSug(cities.length > 0);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input className="form-input" placeholder={placeholder} value={value}
        onChange={e => { onChange(e.target.value); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => search(e.target.value), 400); }}
        onFocus={() => suggestions.length > 0 && setShowSug(true)}
        onBlur={() => setTimeout(() => setShowSug(false), 150)}
        autoComplete="off"/>
      {searching && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--muted)' }}>...</span>}
      {showSug && suggestions.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--card-border)',borderRadius:10,marginTop:2,boxShadow:'0 8px 24px rgba(0,0,0,.6)',overflow:'hidden' }}>
          {suggestions.map(c => (
            <div key={c} style={{ padding:'9px 13px',fontSize:16,cursor:'pointer',borderBottom:'1px solid var(--border)' }}
              onMouseDown={() => { onChange(c); setSuggestions([]); setShowSug(false); }}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddressAutocomplete({ value, onChange, city }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [searching, setSearching] = useState(false);
  const [validated, setValidated] = useState(!!value);
  const timerRef = useRef(null);

  const search = async (q) => {
    if (q.length < 3) { setSuggestions([]); setShowSug(false); return; }
    setSearching(true);
    try {
      const query = city ? `${q}, ${city}` : q;
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&countrycodes=it`,
        { headers: { 'Accept-Language': 'it' } }
      );
      const data = await res.json();
      const addresses = data
        .map(d => {
          const a = d.address;
          const road = a?.road || a?.pedestrian || a?.footway || "";
          const num = a?.house_number ? ` ${a.house_number}` : "";
          return road ? road + num : null;
        })
        .filter(Boolean);
      const unique = [...new Set(addresses)];
      setSuggestions(unique);
      setShowSug(unique.length > 0);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  };

  const hasNumber = (v) => /\d/.test(v);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input className="form-input" placeholder="es. Via Roma 1" value={value}
          onChange={e => { setValidated(false); onChange(e.target.value); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => search(e.target.value), 400); }}
          onFocus={() => suggestions.length > 0 && setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
          autoComplete="off"
          style={validated ? { borderColor:'var(--success)' } : {}}/>
        {searching && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--muted)' }}>...</span>}
        {validated && <span style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'var(--success)' }}>✓</span>}
      </div>
      {/* Suggerimento numero civico */}
      {validated && !hasNumber(value) && (
        <div style={{ fontSize:16,color:'var(--cyan)',marginTop:3 }}>Aggiungi il numero civico per essere più preciso (es. {value} 12)</div>
      )}
      {!validated && value.length > 2 && !searching && suggestions.length === 0 && (
        <div style={{ fontSize:16,color:'var(--magenta)',marginTop:3 }}>Indirizzo non trovato — aggiungi il numero civico o verifica la via</div>
      )}
      {showSug && suggestions.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--card-border)',borderRadius:10,marginTop:2,boxShadow:'0 8px 24px rgba(0,0,0,.6)',overflow:'hidden' }}>
          {suggestions.map((a,i) => (
            <div key={i} style={{ padding:'9px 13px',fontSize:16,cursor:'pointer',borderBottom:'1px solid var(--border)' }}
              onMouseDown={() => { setValidated(true); onChange(a); setSuggestions([]); setShowSug(false); }}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {a}{!hasNumber(a) && <span style={{fontSize:16,color:'var(--muted)',marginLeft:6}}>+ civico?</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VenueEditModal({ venue, onClose, onSave, title="Modifica location" }) {
  const [name,setName]=useState(venue.name);
  const [email,setEmail]=useState(venue.email);
  const [city,setCity]=useState(venue.city||"");
  const [address,setAddress]=useState(venue.address||"");
  const [description,setDescription]=useState(venue.description||"");
  const [equipment,setEquipment]=useState((venue._equipment||[]).map(e=>e.item||e));
  const [backgroundImage,setBackgroundImage]=useState(venue.background_image_url||"");
  const [uploadingImage,setUploadingImage]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const toggleEquip=(item)=>setEquipment(prev=>prev.includes(item)?prev.filter(i=>i!==item):[...prev,item]);
  const handleImageUpload=async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploadingImage(true);setError("");
    try{const url=await uploadVenueImage(file);setBackgroundImage(url);}
    catch{setError("Errore nel caricamento immagine.");}
    finally{setUploadingImage(false);}
  };

  return (
    <Modal onClose={onClose}>
        <div className="modal-title">{title}</div>
        {error&&<div className="alert alert-error">{error}</div>}
        <div className="form-group"><label className="form-label">Nome location</label>
          <input className="form-input" value={name} onChange={e=>setName(e.target.value)}/></div>
        <div className="form-group"><label className="form-label">Email</label>
          <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)}/></div>
        <div className="form-group"><label className="form-label">Città</label>
          <CityAutocomplete value={city} onChange={setCity}/></div>
        <div className="form-group"><label className="form-label">Indirizzo</label>
          <AddressAutocomplete value={address} onChange={setAddress} city={city}/></div>
        <div className="form-group"><label className="form-label">Descrizione</label>
          <textarea className="form-textarea" value={description} onChange={e=>setDescription(e.target.value)}/></div>
        <div className="form-group"><label className="form-label">Sfondo eventi</label>
          <div style={{fontSize:16,color:"var(--muted)",marginBottom:8}}>Immagine usata come sfondo nelle card evento (es. foto del locale).</div>
          {backgroundImage&&<img src={backgroundImage} alt="Sfondo" style={{width:"100%",height:120,objectFit:"cover",borderRadius:10,marginBottom:8,display:"block"}}/>}
          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"10px 14px",borderRadius:10,border:"1px dashed rgba(237,77,29,0.3)",background:"rgba(237,77,29,0.04)"}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span style={{fontSize:16,color:"var(--muted)",fontWeight:600}}>{uploadingImage?"Caricamento...":backgroundImage?"Cambia immagine":"Carica immagine"}</span>
            <input type="file" accept="image/*" style={{display:"none"}} onChange={handleImageUpload} disabled={uploadingImage}/>
          </label>
          {backgroundImage&&<button style={{marginTop:6,background:"none",border:"none",cursor:"pointer",fontSize:16,color:"var(--muted)",padding:0}} onClick={()=>setBackgroundImage("")}>✕ Rimuovi immagine</button>}
        </div>
        <div className="form-group"><label className="form-label">Attrezzatura disponibile</label>
          <div style={{fontSize:16,color:"var(--magenta)",marginBottom:6}}>PA/Casse e Mixer sono obbligatori per ospitare una jam con impianto voce.</div>
          <div className="chip-group">{VENUE_EQUIPMENT_LIST.map(i=>(
            <span key={i} className={`chip ${equipment.includes(i)?"selected":""}`} onClick={()=>toggleEquip(i)}
              style={["PA / Casse","Mixer"].includes(i)?{borderColor:"rgba(237,77,29,.5)"}:{}}>{i}{["PA / Casse","Mixer"].includes(i)?" *":""}</span>
          ))}</div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" disabled={!name.trim()||!email.trim()||loading}
            onClick={async()=>{
              setError("");
              if(!equipment.includes("PA / Casse")||!equipment.includes("Mixer")){setError("PA/Casse e Mixer sono obbligatori.");return;}
              setLoading(true);
              try{await onSave({name:name.trim(),email:email.trim().toLowerCase(),city,address,description,background_image_url:backgroundImage||null},equipment);}
              catch{setError("Errore.");}
              finally{setLoading(false);}
            }}>
            {loading?<span className="spinner"/>:"Salva"}
          </button>
        </div>
    </Modal>
  );
}

export default VenueEditModal;
